import { useRecipientTimeline } from '../../hooks/useOutreach';
import type {
  CampaignRecipient,
  TimelineEvent,
  TimelineEventKind,
} from '../../types/outreach';
import { formatEt, formatEtShort } from '../../utils/formatTimestamp';
import styles from '../pages/OutreachPage.module.css';


/**
 * Drawer showing one recipient's full event timeline. Right-edge slide-in
 * with a chronological feed pulled from /events. Used as the click-target
 * for any recipient row.
 */
export function RecipientTimelineDrawer({
  campaignId,
  recipient,
  onClose,
}: {
  campaignId: number;
  recipient: CampaignRecipient;
  onClose: () => void;
}) {
  const eQ = useRecipientTimeline(campaignId, recipient.id);
  const events = eQ.data || [];

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
        display: 'flex', justifyContent: 'flex-end', zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#FFFFFF', width: 560, maxWidth: '100vw',
          height: '100vh', overflowY: 'auto', padding: 20,
          fontFamily: "'DM Sans', sans-serif",
          boxShadow: '-4px 0 16px rgba(0,0,0,0.12)',
        }}
      >
        <div
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            marginBottom: 16,
          }}
        >
          <div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>
              {[recipient.first_name, recipient.last_name]
                .filter(Boolean).join(' ') || recipient.email}
            </h3>
            <div className={styles.code} style={{ fontSize: 12, color: '#666' }}>
              {recipient.email}
              {recipient.company ? ` · ${recipient.company}` : ''}
            </div>
          </div>
          <button
            type="button"
            className={styles.button}
            onClick={onClose}
          >
            Close
          </button>
        </div>

        {/* Current state summary */}
        <div
          style={{
            background: '#FAF9F4', border: '1px solid #E5E5E0',
            borderRadius: 6, padding: 10, marginBottom: 16, fontSize: 12,
          }}
        >
          <div>
            <strong>Status:</strong> {recipient.status}
            {' · '}
            <strong>Step:</strong> {recipient.current_step}
            {recipient.next_send_at && (
              <>
                {' · '}
                <strong>Next send:</strong>{' '}
                {formatEt(recipient.next_send_at)}
              </>
            )}
          </div>
          {recipient.current_list_status && (
            <div style={{ marginTop: 4, color: '#666' }}>
              Affinity status: {recipient.current_list_status}
            </div>
          )}
        </div>

        <h4 style={{ margin: '16px 0 8px', fontSize: 13, fontWeight: 600 }}>
          Timeline ({events.length} event{events.length === 1 ? '' : 's'})
        </h4>
        {eQ.isLoading ? (
          <div className={styles.empty}>Loading…</div>
        ) : events.length === 0 ? (
          <div className={styles.empty}>
            No events yet. The first one lands when the sequencer picks up
            this recipient on its next eligible tick.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {events.map((e, i) => (
              <TimelineEntry key={i} event={e} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


function TimelineEntry({ event }: { event: TimelineEvent }) {
  const colors: Record<TimelineEventKind, { bg: string; fg: string }> = {
    sent:       { bg: '#E5F4E5', fg: '#2E6E2E' },
    replied:    { bg: '#E5F4E5', fg: '#2A4A8A' },
    bounced:    { bg: '#FCEFEC', fg: '#B04030' },
    opened:     { bg: '#F1EFE5', fg: '#6D5A22' },
    clicked:    { bg: '#E5E8F4', fg: '#2A4A8A' },
    rsvp:       { bg: '#F5E5F4', fg: '#6A2A6A' },
    tick_event: { bg: '#F0F0EC', fg: '#666' },
  };
  const { bg, fg } = colors[event.kind] || { bg: '#F0F0EC', fg: '#666' };
  return (
    <div
      style={{
        display: 'flex', gap: 12, alignItems: 'flex-start',
        padding: 8, borderLeft: `3px solid ${fg}`,
        background: '#FFF',
      }}
    >
      <div style={{ minWidth: 110 }}>
        <div className={styles.code} style={{ fontSize: 10, color: '#888' }}>
          {formatEtShort(event.occurred_at)}
        </div>
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <span
            style={{
              display: 'inline-block', padding: '1px 6px', borderRadius: 8,
              background: bg, color: fg, fontSize: 10,
              fontFamily: "'DM Mono', monospace",
              textTransform: 'uppercase', letterSpacing: '0.04em',
            }}
          >
            {event.kind === 'tick_event' && event.event_type
              ? event.event_type.replace(/_/g, ' ')
              : event.kind}
          </span>
          {event.is_test && (
            <span
              style={{
                display: 'inline-block', padding: '1px 6px', borderRadius: 8,
                background: '#FCEFEC', color: '#B04030', fontSize: 10,
                fontFamily: "'DM Mono', monospace",
                textTransform: 'uppercase', letterSpacing: '0.04em',
              }}
            >
              TEST
            </span>
          )}
        </div>
        <div style={{ fontSize: 13, marginTop: 2 }}>{event.summary}</div>
        {event.detail && (
          <div
            className={styles.code}
            style={{
              fontSize: 11, color: '#555', marginTop: 4,
              maxWidth: '100%', wordBreak: 'break-word', whiteSpace: 'pre-wrap',
            }}
          >
            {event.detail}
          </div>
        )}
        {event.internet_message_id && (
          <div
            className={styles.code}
            style={{ fontSize: 10, color: '#888', marginTop: 2 }}
          >
            imid: {event.internet_message_id}
          </div>
        )}
      </div>
    </div>
  );
}
